import { toaster } from 'components/v2/toast';
import { ProcessStatus, Role } from 'constants/constants';
import { Formik } from 'formik';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { FunctionComponent, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { useTranslation } from 'react-i18next';
import { CreateInvitationModel } from 'services/EpdClient';
import MembershipService from 'services/MembershipService';
import styled from 'styled-components';
import { ErrorText, SuccessText } from 'styles/Styles.styled';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';
import axios, { AxiosError } from 'axios';

interface TProps {
  companyId: string | undefined;
}

const InviteExternalPractitionerTextInput: FunctionComponent<TProps> = ({ companyId }) => {
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [serverError, setServerError] = useState<string>();
  const history = useHistory();

  const { t } = useTranslation();

  const initialValues: CreateInvitationModel = {
    invitedUserEmail: '',
    companyId,
    roleId: Role.EPDDeveloper,
    message: '',
    asGuest: true,
  };

  const inviteSchema = Yup.object({
    invitedUserEmail: Yup.string().email(t('messages.invalidEmail')).required(t('messages.required')),
  });

  const handleInviteUser = async (values: CreateInvitationModel) => {
    setServerError('');
    try {
      setStatus(ProcessStatus.Fetching);
      await MembershipService.createInvitation(values);
      toaster({
        severity: 'success',
        summary: 'An invitation email has been sent to the user.',
      });
      setStatus(ProcessStatus.Success);
    } catch (ex) {
      if (axios.isAxiosError(ex)) {
        const axiosError = ex as AxiosError<{ detail?: string }>;
        if (axiosError.response?.status === 403) {
          if (axiosError.response.data?.detail === 'You do not have permission to view this EPD.') {
            toaster({ severity: 'error', summary: 'Access denied', details: 'You do not have permission to view this EPD.' });
            history.push(EpdLinks.dashboard());
            return;
          }
          toaster({
            severity: 'error',
            summary: 'Error',
            details: 'Only the EPD Owner is allowed to perform this action.',
          });
          setStatus(ProcessStatus.Error);
          return;
        }
      }
      setServerError(PoorMansError(ex));
      setStatus(ProcessStatus.Error);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={inviteSchema}
        onSubmit={(values) => handleInviteUser(values)}
        validateOnChange={false}
      >
        {({ values, errors, setFieldValue, handleSubmit }) => (
          <>
            <InputGroupStyled className="p-inputgroup" disabled={false}>
              <InputTextStyled
                name="invitedUserEmail"
                type="email"
                placeholder="email@example.com"
                onChange={(e: any) => setFieldValue('invitedUserEmail', e.target.value)}
              />
              <InputAddon className="p-inputgroup-addon">
                <StyledInvitePractitionerButton
                  type="submit"
                  label="Send"
                  onClick={() => handleSubmit()}
                  disabled={!values.invitedUserEmail}
                />
              </InputAddon>
            </InputGroupStyled>
            {errors.invitedUserEmail && <ErrorText>{errors.invitedUserEmail}</ErrorText>}
            {status === ProcessStatus.Success && <SuccessText>{t('manageMembers.messages.successInvitation')}</SuccessText>}
          </>
        )}
      </Formik>
      {status === ProcessStatus.Error && (
        <ErrorText>{serverError ? serverError : t('manageMembers.errors.wrong')}</ErrorText>
      )}
    </>
  );
};

const StyledInvitePractitionerButton = styled(Button)`
  height: 1.5rem;
  min-height: 22px;
  background-color: transparent !important;
  color: ${(props) => props.theme.colors.primaryOrange};
  border-radius: 4px;
  border: none;
  padding: 0px;

  &&:hover {
    color: ${(props) => props.theme.colors.elementsButtonHover};
  }

  &:disabled {
    color: ${(props) => props.theme.colors.elementsButtonDisabled};
  }

  ${(props) => props.theme.typography.buttonButtonSmall};
`;

const InputTextStyled = styled(InputText)`
  height: 44px;
  padding: 1rem;
  border: 1px solid
    ${(props) => (props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.elementsInputBorder)};
  border-radius: 2px;
  margin: 0;
  background-color: ${(props) => props.theme.colors.primaryWhite};

  &.p-component.p-disabled {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
  }

  &:focus {
    border-color: ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  &:hover {
    border-color: ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  ${(props) => props.theme.typography.bodyInput};
  border-radius: 2px;
  color: ${(props) => props.theme.colors.primaryBlack};

  &::placeholder {
    color: ${(props) => props.theme.colors.primaryBlack50};
    opacity: 1;
  }
`;

const InputGroupStyled = styled.div<{ disabled: boolean | undefined }>`
  width: auto;
  border: 1px solid ${(props) => (props.disabled ? 'transparent' : props.theme.colors.elementsInputBorder)};
  border-radius: 2px;
  &:disabled {
    border: none;
  }
  &:hover {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }
  .p-inputgroup-addon:last-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }
  ${InputTextStyled} {
    border: none;
    cursor: auto;
    &:focus {
      border: none;
      box-shadow: none;
    }
  }
`;

const InputAddon = styled.span<{ disabled?: boolean | undefined }>`
  max-width: 60px;
  border: none;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};
  cursor: auto;
  padding: 0.5rem 1rem 0.5rem 0rem;
  min-width: 0;
  .pi {
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 700;
  }
`;

export default InviteExternalPractitionerTextInput;
